import _ from 'lodash';
import getStroke from 'perfect-freehand';
import { drawing } from './drawing';
import { fabric } from './fabric';

export const presetColors = [
  '#FFFFFF', '#F2F2F2', '#CCCCCC', '#AAAAAA', '#111111',
  '#FFFFCC', '#FFBEDA', '#FF00CC', '#FF0202', '#800000',
  '#FF570D', '#FFAB00', '#FFEA00', '#99CC00', '#1BFF00',
  '#008000', '#0034FF', '#00A3FF', '#D7EEFF', '#8900FF'
];

export const presetStrokeSize = [
  1, 4, 8, 12, 16, 20, 32,
];

export const defaultColor = presetColors[4];

export const defaultStrokeSize = presetStrokeSize[1];

export const strokeSettings = {
  thinning: 0.5,
  smoothing: 0.5,
  streamline: 0.5,
}

export const getSvgPathFromStroke = (points) => {
  if (!points.length) {
    return '';
  }
  const d = points.map(([x, y], i) => (i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`));
  d.push('Z');
  return d.join(' ');
}

export const finalizeStroke = async ({ fabricCanvas }) => {
  const state = drawing.freehandState;
  state.tempPaths.forEach((p) => {
    if (p) {
      fabricCanvas.remove(p);
    }
  });

  const pathObjects = [];

  for (const strokePoints of state.allPoints) {
    if (strokePoints.length < 2) {
      continue;
    }

    const stroke = getStroke(strokePoints, {
      size: state.strokeSize,
      thinning: 0.5,
      smoothing: 0.5,
      streamline: 0.5,
    });

    const pathData = getSvgPathFromStroke(stroke);
    const path = new fabric.Path(pathData, {
      fill: state.color,
      selectable: false,
      evented: false,
    });
    path.set({ dataPoints: strokePoints });

    pathObjects.push(path);
  }

  if (pathObjects.length === 0) {
    return;
  }

  const group = new fabric.CustomFreehand(pathObjects, {
    selectable: true,
    evented: true,
    strokeSize: state.strokeSize,
    strokeColor: state.color,
  });

  fabricCanvas.add(group);
  fabricCanvas.renderAll();

  drawing.client.push({
    action: 'add',
    objects: [group],
  });
  await drawing.client.flush();

  var history = drawing.historyInit();
  drawing.historyPush(group, history);
  drawing.historySet('add', history);

  state.points = [];
  state.allPoints = [];
  state.tempPaths = [];
};

export function freehandStart({ pointer, fabricCanvas }) {
  const state = drawing.freehandState;
  state.isDrawing = true;
  fabricCanvas.discardActiveObject();
  fabricCanvas.renderAll();
  state.points = [[pointer.x, pointer.y]];
  state.tempPaths.push(null);
}

export function freehandMove({ pointer, fabricCanvas }) {
  const state = drawing.freehandState;

  if (!state.isDrawing) {
    return;
  }

  state.points.push([pointer.x, pointer.y]);

  const stroke = getStroke(state.points, {
    size: state.strokeSize,
    thinning: strokeSettings.thinning,
    smoothing: strokeSettings.smoothing,
    streamline: strokeSettings.streamline,
  });

  const pathData = getSvgPathFromStroke(stroke);

  const existingPath = state.tempPaths[state.tempPaths.length - 1];
  if (existingPath) {
    fabricCanvas.remove(existingPath);
  }

  const tempPath = new fabric.Path(pathData, {
    fill: state.color,
    selectable: false,
    evented: false,
  });

  fabricCanvas.add(tempPath);
  state.tempPaths[state.tempPaths.length - 1] = tempPath;
  state.redoStack = [];
  fabricCanvas.renderAll();
}

export function freehandEnd() {
  const state = drawing.freehandState;

  if (!state.isDrawing) {
    return;
  }

  state.isDrawing = false;

  if (state.points.length > 0) {
    state.allPoints.push([...state.points]);
  }

  state.points = [];
}

export const penStart = ({ fabricCanvas }) => {
  const state = drawing.freehandState;
  fabricCanvas.selection = false;
  fabricCanvas.skipTargetFind = true;
  fabricCanvas.defaultCursor = 'default';

  state.allPoints = [];
  state.points = [];
  state.tempPaths.forEach((p) => {
    if (p) {
      fabricCanvas.remove(p);
    }
  });
  state.tempPaths = [];
}

export const penEnd = (() => {
  let isRunning = false;
  return async ({ fabricCanvas }) => {
    if (isRunning) {
      return;
    }
    isRunning = true;

    try {
      const state = drawing.freehandState;
      fabricCanvas.selection = false;
      fabricCanvas.skipTargetFind = false;
      fabricCanvas.defaultCursor = 'default';

      state.isDrawing = false;
      await finalizeStroke({ fabricCanvas });
    } finally {
      isRunning = false;
    }
  }
})();

export function updateFreehandGroupThickness(group, newSize) {
  group._objects.forEach(path => {
    const originalPoints = path.dataPoints;
    if (!originalPoints) {
      return;
    }

    const stroke = getStroke(originalPoints, {
      size: newSize,
      thinning: strokeSettings.thinning,
      smoothing: strokeSettings.smoothing,
      streamline: strokeSettings.streamline,
    });

    const newPathData = getSvgPathFromStroke(stroke);
    path.set({ path: new fabric.Path(newPathData).path });
  });

  group.strokeSize = newSize;
  group.setCoords();
  group.canvas.requestRenderAll();
}

export function updateCustomFreehandColor(group, newColor) {
  group._objects.forEach(path => {
    path.fill = newColor;
  });

  group.strokeColor = newColor;
  group.canvas.requestRenderAll();
}

export const handleUndoPath = () => {
  const fabricCanvas = drawing._canvas;
  const { tempPaths, redoStack, allPoints } = drawing.freehandState;
  if (tempPaths.length > 0) {
    const lastPath = tempPaths.pop();
    if (lastPath) {
      fabricCanvas.remove(lastPath);
      const lastPoints = allPoints.pop();
      redoStack.push({ path: lastPath, points: lastPoints });
      fabricCanvas.renderAll();
    }
  }
};

export const handleRedoPath = () => {
  const fabricCanvas = drawing._canvas;
  const { tempPaths, redoStack, allPoints } = drawing.freehandState;
  if (redoStack.length > 0) {
    const { path, points } = redoStack.pop();
    if (path) {
      fabricCanvas.add(path);
      tempPaths.push(path);
      allPoints.push(points);
      fabricCanvas.renderAll();
    }
  }
};

export function canUndoPen() {
  return drawing.freehandState.tempPaths.some(p => p != null);
}

export function canRedoPen() {
  return drawing.freehandState.redoStack.length > 0;
}


// WEBPACK FOOTER //
// ./src/canvas/common/freehandUtil.js